// Cap slider bounds in settings UI.
export const MIN_CAP_PERCENTAGE = 1
export const MAX_CAP_PERCENTAGE = 100

// Backend stores request deadline day (1..27). UI shows cycle closeout day (2..28).
// No wrap logic: closeout day is always the day after the request deadline.
const MIN_REQUEST_DEADLINE_DAY = 1
const MAX_REQUEST_DEADLINE_DAY = 27
export const MIN_CLOSEOUT_DAY = MIN_REQUEST_DEADLINE_DAY + 1
export const MAX_CLOSEOUT_DAY = MAX_REQUEST_DEADLINE_DAY + 1

export const toCloseoutDay = (requestDeadlineDay: number) => {
  return requestDeadlineDay + 1
}

export const toRequestDeadlineDay = (closeoutDay: number) => {
  return closeoutDay - 1
}

export const normalizeCapPercentage = (capPercentage: number | null | undefined) => {
  if (capPercentage == null || !Number.isFinite(capPercentage)) {
    return null
  }

  return Math.max(MIN_CAP_PERCENTAGE, Math.min(capPercentage, MAX_CAP_PERCENTAGE))
}

export const ACCRUAL_MODE_CALENDAR_ONLY = 'calendar_only'
export const ACCRUAL_MODE_ATTENDANCE_CONNECTED = 'attendance_connected'

const ACCRUAL_MODES = [ACCRUAL_MODE_CALENDAR_ONLY, ACCRUAL_MODE_ATTENDANCE_CONNECTED] as const

export type AccrualMode = (typeof ACCRUAL_MODES)[number]

export const isAccrualMode = (value: string): value is AccrualMode => {
  return ACCRUAL_MODES.some((accrualMode) => accrualMode === value)
}

const SALARY_FREQUENCY_HOURLY = 'hourly'
const SALARY_FREQUENCY_DAILY = 'daily'
const SALARY_FREQUENCY_WEEKLY = 'weekly'
const SALARY_FREQUENCY_MONTHLY = 'monthly'
const SALARY_FREQUENCY_YEARLY = 'yearly'

export const ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES = [
  SALARY_FREQUENCY_HOURLY,
  SALARY_FREQUENCY_DAILY,
  SALARY_FREQUENCY_WEEKLY,
  SALARY_FREQUENCY_MONTHLY,
  SALARY_FREQUENCY_YEARLY,
] as const

export type AttendanceEligibleSalaryFrequency =
  (typeof ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES)[number]

const DEFAULT_ATTENDANCE_ACCRUAL_FREQUENCIES: AttendanceEligibleSalaryFrequency[] = [
  SALARY_FREQUENCY_HOURLY,
]

const isAttendanceEligibleSalaryFrequency = (
  value: string
): value is AttendanceEligibleSalaryFrequency => {
  return ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES.some((frequency) => frequency === value)
}

export const splitAttendanceAccrualFrequencies = (
  values: string[] | null | undefined
): {
  supported: AttendanceEligibleSalaryFrequency[]
  unsupported: string[]
} => {
  if (!values) {
    return {
      supported: DEFAULT_ATTENDANCE_ACCRUAL_FREQUENCIES,
      unsupported: [],
    }
  }

  const deduplicated = Array.from(new Set(values))
  const supported = deduplicated.filter(isAttendanceEligibleSalaryFrequency)
  const unsupported = deduplicated.filter(
    (value): value is string => !isAttendanceEligibleSalaryFrequency(value)
  )

  return {
    supported,
    unsupported,
  }
}
